import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid2 as Grid,
  InputAdornment,
  Modal,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../../../utils/GlobalContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/functions";
import axiosMain from "../../../http/axiosMain";
import { DropDown } from "../../Components/DropDown";
import { InputLabel } from "../../Components/InputLabel";
import { NumberField } from "../../Components/NumberField";
import { modalStyle, theme } from "../constant";
import { setupProfileSchema } from "./schema";

/** Icons */
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { IoChevronBackOutline, IoClose } from "react-icons/io5";
import { MdOutlineCloudUpload, MdVerified } from "react-icons/md";
import { SocialMediaDetails } from "../../../../utils/constants";
import userProfileImage from "../../../Assets/Img/userprofileimg.png";
import { BoxWithBorder } from "../../Components/BoxWithBorder";
import { InputWithLabel } from "../../Components/InputWithLabel";
import { ProgressBar } from "../../Components/ProgresBar";
import "./style.css";

const socialLinksMaster = [
  "instagram",
  "youtube",
  "facebook",
  "linkedin",
  "snapchat",
  "twitch",
  "twitter",
  "reddit",
  "quora",
];

export const SetupBrandProfile = ({ open, onClose, isEdit = false }) => {
  const { userData, setUserData } = useContext(GlobalContext);
  const totalStep = isEdit ? 4 : 5;
  const [socialLoading, setSocialLoading] = useState(null);
  const [file, setFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const inputFileRef = useRef();





  const fetchStates = async () => {
    try {
      const response = await axiosMain.get("/api/v1/states");
      if (response.data?.states && Array.isArray(response.data.states)) {
        setStates(
          response.data.states.map(({ id, name }) => ({
            label: name,
            value: name,
            id,
          }))
        );
      } else {
        console.error("Invalid state data received:", response.data);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchCities = async (state) => {
    try {
      const stateId = states.find((i) => i.value === state)?.id;
      const response = await axiosMain.get(`/api/v1/states/${stateId}/cities`);
      if (response.data?.cities && Array.isArray(response.data.cities)) {
        setCities(response.data.cities.map((i) => ({ label: i, value: i }))); // ✅ Update cities state
      } else {
        console.error("No cities found or invalid response structure");
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosMain.get("/api/v1/get-categories");
      if (
        response.data?.categories &&
        Array.isArray(response.data.categories)
      ) {
        setCategories(
          response.data.categories.map((i) => ({ label: i, value: i }))
        );
      } else {
        console.error("Invalid category format received:", response.data);
        setCategories([]); // Fallback to empty array
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]); // Fallback to empty array
    }
  };

  const validateInstagramURL = async (type, username) => {
    try {
      const usernameRegex = /^@[A-Za-z0-9._]{1,29}$/;

      // If no error after validation, send email
      const isValid = usernameRegex.test(username);
      if (isValid) {
        setSocialLoading(type);
        const response = await axiosMain.post(
          "/api/statistics/instagram/validate-username",
          { type, username }
        );

        if (response?.data?.success === false) {
          setFieldValue(`socialLinks.${type}Verified`, false);
          setFieldError(
            `socialLinks.${type}`,
            response.data.message || "Validation failed"
          ); // Show server message
        } else {
          if (response?.data?.data?.id) {
            setFieldValue(`socialLinks.${type}Verified`, true);
            setFieldError(`socialLinks.${type}`, ""); // Clear previous error
          } else {
            setFieldValue(`socialLinks.${type}Verified`, false);
            setFieldError(
              `socialLinks.${type}`,
              "Invalid category format received."
            );
          }
        }
      } else {
        setFieldError(`socialLinks.${type}`, "Instagram Profile is invalid.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setFieldValue(`socialLinks.${type}Verified`, false);
      setFieldError(
        `socialLinks.${type}`,
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong"
      );
    } finally {
      setSocialLoading(false);
    }
  };

  // API Call
  useEffect(() => {
    fetchStates();
    fetchCategories();
  }, []);

  const getBrandProfile = async () => {
    try {
      const profileResponse = await axiosMain.get("/api/v1/get-brand-profile");
      if (profileResponse.status === 200 && profileResponse.data?.user) {
        setUserData(profileResponse.data.user); // Update GlobalContext
      }
    } catch (error) {
      console.error("Error fetching brand profile:", error);
    }
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldValue,
    setFieldError,
    validateForm,
    setErrors,
  } = useFormik({
    initialValues: {
      filePreview: userData?.user_photo,
      firstName: userData?.first_name,
      lastName: userData?.last_name,
      brandName: userData?.brand_name,
      registeredBy: [userData?.first_name, userData?.last_name]
        .filter(Boolean)
        .join(" "),
      age: userData?.age,
      position: userData?.position,
      category: userData?.categories || [],
      bio: userData?.bio,
      phoneNo: userData?.phone_no,
      email: userData?.email,
      state: userData?.state,
      city: userData?.city,
      address: userData?.address,
      // addressLine2: userData?.addressLine2,
      addressLine2: userData?.address_line_2 || "",

      pincode: userData?.pincode,
      website: userData?.brand_website || "",
      socialLinks: socialLinksMaster.reduce((acc, key) => {
        const linkData = userData?.socialLinks?.find((i) => i.type === key);

        return {
          ...acc,
          [key]: linkData?.link,
          [`${key}Verified`]: linkData?.verified || false,
        };
      }, {}),
    },
    
    // enableReinitialize: true,
    validateOnChange: false,
    validationSchema: setupProfileSchema(currentStep, userData?.role),
    onSubmit: async (data) => {
      try {
        setCurrentStep((prev) => {
          return prev + 1;
        });
        const formattedValue = {
          brand_name: data.brandName,
          position: data.position,
          first_name:
            userData?.role === "influencer"
              ? data.firstName
              : data.registeredBy?.split(" ")?.[0] || "",
          last_name:
            userData?.role === "influencer"
              ? data.lastName
              : data.registeredBy?.split(" ")?.[1] || "",
          age: data.age,
          categories: data.category,
          bio: data.bio,
          brand_website: data.website,
          city: data.city,
          address: data.address,
          addressLine2: data.addressLine2,
          state: data.state,
          pincode: data.pincode,
          phone_no: data.phoneNo,
          email: data.email,
          socialLinks: JSON.stringify(
            Object.keys(data.socialLinks).reduce((acc, link) => {
              if (data.socialLinks[link] && !link.includes("Verified")) {
                return {
                  ...acc,
                  [link]: data?.socialLinks?.[link],
                  [`${link}Verified`]:
                    data?.socialLinks?.[`${link}Verified`] || false,
                };
              }
              return acc;
            }, {})
          ),
        };

        const formData = new FormData();
        if (file) {
          formData.append("file", file);
        }
        Object.keys(formattedValue).forEach((key) => {
          formData.append(key, formattedValue[key]);
        });

        if (userData?.role === "brand") {
          formData.delete("age");
        } else if (userData?.role === "influencer") {
          formData.delete("brand_name");
          formData.delete("position");
        }

        if (isEdit) {
          await axiosMain.put("/api/v1/brand-profile", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          await axiosMain.post("/api/v1/brand-profile", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          setCurrentStep(5);
        }

        showSuccessToast("Profile setup successfully.");
        getBrandProfile();
        if (isEdit) {
          onClose("save");
        } else {
          setTimeout(() => {
            onClose("save");
          }, 5000);
        }
      } catch (err) {
        console.error("Error submitting form:", err);
        showErrorToast(err.response.data.error || err.message);
        setCurrentStep(1);
      }
    },
  });
    useEffect(() => {
  if (states.length && values.state) {
    fetchCities(values.state);
  }
}, [values.state]);

  useEffect(() => {
    if (states.length && (values.state || userData?.state)) {
      fetchCities(values.state || userData.state);
    }
  }, [values.state, userData?.state, states]);

  const handleNext = async (movement) => {
    const errors = await validateForm();
    if (Object.keys(errors).length && movement === "next") {
      setErrors(errors);
    } else {
      if (movement === "next") {
        if (currentStep === 3) {
          handleSubmit();
        } else {
          setCurrentStep((prev) => {
            return prev + 1;
          });
        }
      } else if (movement === "previous") {
        setCurrentStep((prev) => {
          return prev - 1;
        });
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setFieldValue("filePreview", URL.createObjectURL(file));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal open={open}>
        <Box sx={{ ...modalStyle, background: "#fff" }}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            rowGap={"20px"}
            position={"relative"}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "24px",
                  textAlign: "center",
                  color: "#000",
                  fontWeight: 600,
                }}
              >
                {isEdit ? "Edit Profile" : "Let’s setup your Profile!"}
              </Typography>
              {isEdit ? (
                <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                  <IoClose
                    size={28}
                    style={{ cursor: "pointer" }}
                    onClick={() => onClose("manual")}
                  />
                </Box>
              ) : (
                <></>
              )}
            </Box>
            <ProgressBar progress={(100 / totalStep) * currentStep} />
            <Box>
              {currentStep === 1 ? (
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <BoxWithBorder title={"Basic Details"}>
                      <Grid container spacing={3}>
                        <Grid size={12}>
                          <Box
                            display={"flex"}
                            flexDirection={"column"}
                            rowGap={2}
                          >
                            <Box display={"flex"} alignItems={"center"} gap={3}>
                              <input
                                type="file"
                                accept="image/*"
                                id="fileInput"
                                onChange={handleImageChange}
                                classBio="d-none drop"
                                hidden
                                ref={inputFileRef}
                              />
                              {values.filePreview ? (
                                <Box position={"relative"}>
                                  <img
                                    src={values.filePreview || userProfileImage}
                                    alt="User Profile"
                                    style={{
                                      objectFit: "cover",
                                      width: "120px",
                                      height: "120px",
                                      borderRadius: "50%",
                                      border: "2px solid transparent",
                                      ...(errors.file
                                        ? {
                                            border: "2px solid red",
                                          }
                                        : {}),
                                    }}
                                  />
                                  {values.filePreview ? (
                                    <Tooltip
                                      title={"Edit Profile"}
                                      placement="bottom"
                                    >
                                      <Box
                                        width={"30px"}
                                        height={"30px"}
                                        sx={{
                                          position: "absolute",
                                          bottom: "5%",
                                          right: "5%",
                                          background: "rgb(236, 236, 236)",
                                          borderRadius: "50%",
                                          border: "2px solid #ddd",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          inputFileRef.current.click()
                                        }
                                      >
                                        <EditRoundedIcon
                                          sx={{ fontSize: "14px" }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ position: "relative" }}>
                                  <Box
                                    width={"120px"}
                                    height={"120px"}
                                    className="profile-image-border"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexDirection: "column",
                                      color: "#6B8E76",
                                      gap: "12px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => inputFileRef.current.click()}
                                  >
                                    <MdOutlineCloudUpload size={36} />
                                    <Typography
                                      fontSize={"12px"}
                                      fontWeight={600}
                                    >
                                      Upload Photo
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              <Box
                                display={"flex"}
                                flexDirection={"column"}
                                rowGap={"8px"}
                                sx={{
                                  fontSize: "16px",
                                  color: "#3C3C4399",
                                  fontWeight: 400,
                                }}
                              >
                                <Typography>
                                  Upload Your Profile Photo here
                                </Typography>
                                <Typography>Max 5 MB files allowed</Typography>
                                <Typography>
                                  Supported File : .jpg, .jpeg,.png
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <FormHelperText
                                error={Boolean(!!errors.filePreview)}
                              >
                                {errors.filePreview}
                              </FormHelperText>
                            </Box>
                          </Box>
                        </Grid>
                        {userData?.role === "brand" ? (
                          <Grid size={6}>
                            <InputWithLabel
                              label={"Brand Name"}
                              value={values.brandName}
                              onChange={handleChange}
                              name={"brandName"}
                              error={errors.brandName}
                              placeholder={"Enter Brand Name"}
                            />
                          </Grid>
                        ) : (
                          <></>
                        )}
                        {userData?.role === "influencer" ? (
                          <>
                            <Grid size={6}>
                              <InputWithLabel
                                label={"First Name"}
                                value={values.firstName}
                                onChange={handleChange}
                                name={"firstName"}
                                error={errors.firstName}
                                placeholder={"Enter First Name"}
                              />
                            </Grid>
                            <Grid size={6}>
                              <InputWithLabel
                                label={"Last Name"}
                                value={values.lastName}
                                onChange={handleChange}
                                name={"lastName"}
                                error={errors.lastName}
                                placeholder={"Enter Last Name"}
                              />
                            </Grid>
                            <Grid size={6}>
                              <Box
                                display={"flex"}
                                alignItems={"start"}
                                justifyContent={"center"}
                                flexDirection={"column"}
                                rowGap={0.5}
                              >
                                <InputLabel label={"Age"} />
                                <NumberField
                                  placeholder={"Enter Age"}
                                  error={errors.age}
                                  value={values.age}
                                  maxLength={2}
                                  handleChange={handleChange}
                                  setError={setFieldError}
                                  name={"age"}
                                />
                              </Box>
                            </Grid>
                          </>
                        ) : (
                          <></>
                        )}
                        <Grid size={6}>
                          <Box
                            display={"flex"}
                            alignItems={"start"}
                            justifyContent={"center"}
                            flexDirection={"column"}
                            rowGap={0.5}
                          >
                            <InputLabel
                              label={
                                userData?.role === "brand"
                                  ? "Select Brand Category(ies)"
                                  : "Select Category(ies)"
                              }
                            />
                            <DropDown
                              name="category"
                              label={"Select Brand Category(ies)"}
                              options={categories}
                              multiple
                              onChange={handleChange}
                              value={values.category}
                              error={Boolean(!!errors.category)}
                              helperText={errors.category}
                            />
                          </Box>
                        </Grid>
                        {userData?.role === "brand" ? (
                          <>
                            <Grid size={6}>
                              <InputWithLabel
                                label={"Registered By"}
                                value={values.registeredBy}
                                onChange={handleChange}
                                name={"registeredBy"}
                                error={errors.registeredBy}
                                placeholder={"Enter Your Name"}
                              />
                            </Grid>
                            <Grid size={6}>
                              <InputWithLabel
                                label={"Designation"}
                                value={values.position}
                                onChange={handleChange}
                                name={"position"}
                                error={errors.position}
                                placeholder={"Enter Your Designation"}
                              />
                            </Grid>
                          </>
                        ) : (
                          <></>
                        )}
                        <Grid size={12}>
                          <InputWithLabel
                            label={
                              userData?.role === "brand" ? "Brand's Bio" : "Bio"
                            }
                            value={values.bio}
                            onChange={handleChange}
                            name={"bio"}
                            error={errors.bio}
                            placeholder={"Enter description"}
                          />
                        </Grid>
                      </Grid>
                    </BoxWithBorder>
                  </Grid>
                </Grid>
              ) : (
                <></>
              )}
              {currentStep === 2 ? (
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <BoxWithBorder title={"Contact Details"}>
                      <Grid container spacing={3}>
                        <Grid size={6}>
                          <Box
                            display={"flex"}
                            alignItems={"start"}
                            justifyContent={"center"}
                            flexDirection={"column"}
                            rowGap={0.5}
                          >
                            <InputLabel label={"Phone Number"} />
                            <NumberField
                              placeholder={"Enter Phone Number"}
                              error={errors.phoneNo}
                              value={values.phoneNo}
                              maxLength={10}
                              handleChange={handleChange}
                              setError={setFieldError}
                              name={"phoneNo"}
                            />
                          </Box>
                        </Grid>
                        <Grid size={6}>
                          <InputWithLabel
                            label={"Email"}
                            value={values.email}
                            onChange={handleChange}
                            name={"email"}
                            error={errors.email}
                            placeholder={"Enter Email"}
                          />
                        </Grid>
                      </Grid>
                    </BoxWithBorder>
                  </Grid>
                  <Grid size={12}>
                    <BoxWithBorder title={"Address"}>
                      <Grid container spacing={3}>
                        <Grid size={6}>
                          <InputWithLabel
                            label={"Address Line 1"}
                            value={values.address}
                            onChange={handleChange}
                            name={"address"}
                            error={errors.address}
                            placeholder={"Enter Address"}
                          />
                        </Grid>
                        <Grid size={6}>
                          <InputWithLabel
                            label={"Address Line 2"}
                            value={values.addressLine2}
                            onChange={handleChange}
                            name={"addressLine2"}
                            error={errors.addressLine2}
                            placeholder={"Enter Address Line 2"}
                          />
                        </Grid>
                        <Grid size={4}>
                          <Box
                            display={"flex"}
                            alignItems={"start"}
                            justifyContent={"center"}
                            flexDirection={"column"}
                            rowGap={0.5}
                          >
                            <InputLabel label={"State"} />
                            <DropDown
                              name={"state"}
                              label={"Select State"}
                              options={states}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue("city", "");
                              }}
                              value={values.state}
                              error={Boolean(!!errors.state)}
                              helperText={errors.state}
                            />
                          </Box>
                        </Grid>
                        <Grid size={4}>
                          <Box
                            display={"flex"}
                            alignItems={"start"}
                            justifyContent={"center"}
                            flexDirection={"column"}
                            rowGap={0.5}
                          >
                            <InputLabel label={"City"} />
                            <DropDown
                              name="city"
                              label={"Select City"}
                              options={cities}
                              onChange={handleChange}
                              value={values.city}
                              error={Boolean(!!errors.city)}
                              helperText={errors.city}
                            />
                          </Box>
                        </Grid>
                        <Grid size={4}>
                          <Box
                            display={"flex"}
                            alignItems={"start"}
                            justifyContent={"center"}
                            flexDirection={"column"}
                            rowGap={0.5}
                          >
                            <InputLabel label={"Pincode"} />
                            <NumberField
                              placeholder={"Enter Pincode"}
                              error={errors.pincode}
                              value={values.pincode}
                              maxLength={6}
                              handleChange={handleChange}
                              setError={setFieldError}
                              name={"pincode"}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </BoxWithBorder>
                  </Grid>
                </Grid>
              ) : (
                <></>
              )}
              {currentStep === 3 ? (
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <BoxWithBorder title={"Website Link"} subTitle="Optional">
                      <Grid container spacing={3}>
                        <Grid size={12}>
                          <InputWithLabel
                            value={values.website}
                            onChange={handleChange}
                            name={"website"}
                            error={errors.website}
                            placeholder={"Enter Your Website Link"}
                          />
                        </Grid>
                      </Grid>
                    </BoxWithBorder>
                  </Grid>
                  <Grid size={12}>
                    <BoxWithBorder title={"Social Media Handles"}>
                      <Grid container spacing={3}>
                        <Grid size={12}>
                          <Grid
                            container
                            width={"100%"}
                            spacing={2}
                            marginTop={"0.5rem"}
                          >
                            {Object.keys(values.socialLinks).map((link) => {
                              if (!socialLinksMaster.includes(link)) {
                                return <></>;
                              }

                              const { icon, key, placeholder } =
                                SocialMediaDetails[link];

                              return (
                                <Grid size={{ xs: 6, md: 4 }} key={key}>
                                  <TextField
                                    size="small"
                                    placeholder={placeholder}
                                    name={`socialLinks.${link}`}
                                    value={values.socialLinks[link]}
                                    onChange={(e) => {
                                      setFieldValue(
                                        `socialLinks.${link}Verified`,
                                        false
                                      );
                                      setFieldError(
                                        `socialLinks.${link}`,
                                        null
                                      );
                                      setSocialLoading(false);
                                      handleChange(e);
                                    }}
                                    sx={{
                                      width: "100%",

                                      "& .MuiInputBase-root": {
                                        height: "48px",
                                      },
                                    }}
                                    slotProps={{
                                      input: {
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            {icon({ size: "24px" })}
                                          </InputAdornment>
                                        ),
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            {values.socialLinks[link] &&
                                            !values.socialLinks[
                                              `${link}Verified`
                                            ] &&
                                            socialLoading !== link ? (
                                              <Typography
                                                fontSize={"12px"}
                                                fontWeight={500}
                                                sx={{
                                                  cursor: "pointer",
                                                }}
                                                onClick={() => {
                                                  if (link === "instagram") {
                                                    validateInstagramURL(
                                                      link,
                                                      values.socialLinks[link]
                                                    );
                                                  }
                                                }}
                                              >
                                                Connect
                                              </Typography>
                                            ) : (
                                              <></>
                                            )}
                                            {values.socialLinks[link] &&
                                            values.socialLinks[
                                              `${link}Verified`
                                            ] ? (
                                              <MdVerified color="green" />
                                            ) : (
                                              <></>
                                            )}
                                            {values.socialLinks[link] &&
                                            !values.socialLinks[
                                              `${link}Verified`
                                            ] &&
                                            socialLoading === link ? (
                                              <CircularProgress
                                                color="green"
                                                size={12}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </InputAdornment>
                                        ),
                                      },
                                    }}
                                    error={
                                      Boolean(!!errors.socialLinks?.[link]) ||
                                      Boolean(
                                        !!errors.socialLinks?.[
                                          `${link}Verified`
                                        ]
                                      )
                                    }
                                    helperText={
                                      errors.socialLinks?.[link] ||
                                      errors.socialLinks?.[`${link}Verified`]
                                    }
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </BoxWithBorder>
                  </Grid>
                </Grid>
              ) : (
                <></>
              )}
              {currentStep === 4 ? (
                <Box
                  sx={{
                    minHeight: "50vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  <CircularProgress
                    size={"48px"}
                    sx={{
                      marginBottom: "1rem",
                    }}
                  />
                  <Typography fontWeight={500} fontSize={20}>
                    Setting things up for you
                  </Typography>
                  <Typography fontWeight={400} fontSize={"14px"}>
                    This won’t take long
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              {!isEdit && currentStep === 5 ? (
                <Box
                  sx={{
                    minHeight: "50vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  <Typography fontWeight={500} fontSize={20}>
                    Your Profile has been sent for verification!
                  </Typography>
                  <Typography fontWeight={400} fontSize={"14px"}>
                    We will Get back to you soon...
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Box>
            {currentStep !== 4 && currentStep !== 5 ? (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={currentStep !== 1 ? "space-between" : "end"}
              >
                {currentStep !== 1 ? (
                  <Button variant="text" onClick={() => handleNext("previous")}>
                    <IoChevronBackOutline style={{ marginRight: "6px" }} />
                    Back
                  </Button>
                ) : (
                  <></>
                )}
                <Button variant="contained" onClick={() => handleNext("next")}>
                  Save & Next
                </Button>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};
